/**
 * Fixed Route UAT Validation Utilities
 * 
 * Provides validation functions for route uploads to UAT:
 * - Dry-run payload generation
 * - Duplicate detection
 * - Version increment logic
 * - Coordinate matching for stops
 */

import type { FixedRoute, FixedRouteStop } from '../types/fixedRoutes';
import { exportRouteData, validateRouteExport, transformToUATRoutePayload } from './fixedRouteDataExporter';
import { fetchOperationsSolutions, type UATOperationsSolutionsResponse, type UATStop } from './fixedRouteUatClient';
import { extractUATStops } from './fixedRouteUatImporter';

export interface DuplicateRoute {
  routeName: string;
  existingRouteId: string;
  existingRouteLabel: string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  duplicates: DuplicateRoute[];
  dryRunPayloads: any[];
  suggestedVersionNames: Map<string, string>; // routeId -> version name
}

/**
 * Generate dry-run payloads for selected routes
 * 
 * @param routes - Routes to generate payloads for
 * @param stops - All available stops
 * @param networkId - Target network ID
 * @param description - Route description
 * @returns Array of payloads ready for UAT API
 */
export function generateDryRunPayloads(
  routes: FixedRoute[],
  stops: FixedRouteStop[],
  networkId: number,
  description: string = ''
): any[] {
  return routes.map(route => {
    const exportedData = exportRouteData(route, stops);
    return transformToUATRoutePayload(
      exportedData,
      String(networkId),
      'Version 1',
      description
    );
  });
}

/**
 * Check for duplicate routes in UAT
 * 
 * @param jwtToken - JWT token
 * @param productId - Product ID
 * @param routes - Routes to check
 * @param date - Service date (YYYY-MM-DD) - optional, skips duplicate check if not provided
 * @param timezone - Timezone string - optional, skips duplicate check if not provided
 * @returns Array of duplicate route information
 */
export async function checkForDuplicates(
  jwtToken: string,
  productId: string,
  routes: FixedRoute[],
  date?: string,
  timezone?: string
): Promise<DuplicateRoute[]> {
  if (!date || !timezone) {
    // Skip duplicate detection if date/timezone not provided
    // This is acceptable for route-only uploads
    return [];
  }

  try {
    const response: UATOperationsSolutionsResponse = await fetchOperationsSolutions(
      jwtToken,
      productId,
      date,
      timezone
    );

    const duplicates: DuplicateRoute[] = [];
    const routeNames = new Set(routes.map(r => r.name.toLowerCase()));

    // Check each entry for matching route names
    response.data.entries.forEach(entry => {
      if (entry.shiftSolutions) {
        entry.shiftSolutions.forEach(shiftSolution => {
          const routeLabel = shiftSolution.route?.label;
          if (routeLabel && routeNames.has(routeLabel.toLowerCase()) && shiftSolution.route) {
            // Find the matching route
            const matchingRoute = routes.find(r => r.name.toLowerCase() === routeLabel.toLowerCase());
            if (matchingRoute) {
              duplicates.push({
                routeName: matchingRoute.name,
                existingRouteId: shiftSolution.route.id,
                existingRouteLabel: routeLabel,
              });
            }
          }
        });
      }
    });

    return duplicates;
  } catch (error) {
    // If duplicate check fails, log but don't block upload
    console.warn('Failed to check for duplicates:', error);
    return [];
  }
}

/**
 * Generate suggested version names for routes with duplicates
 * 
 * @param routes - Routes to generate version names for
 * @param duplicates - Array of duplicate route information
 * @returns Map of routeId -> suggested version name
 */
export function generateVersionNames(
  routes: FixedRoute[],
  duplicates: DuplicateRoute[]
): Map<string, string> {
  const versionMap = new Map<string, string>();
  const duplicateNames = new Set(duplicates.map(d => d.routeName.toLowerCase()));

  routes.forEach(route => {
    if (duplicateNames.has(route.name.toLowerCase())) {
      // Find existing versions and increment
      // For now, use a simple increment pattern
      // In a real scenario, we'd query UAT for existing versions
      versionMap.set(route.id, 'Version 2');
    } else {
      versionMap.set(route.id, 'Version 1');
    }
  });

  return versionMap;
}

/**
 * Comprehensive validation for route uploads
 * 
 * @param routes - Routes to validate
 * @param stops - All available stops
 * @param networkId - Target network ID
 * @param jwtToken - JWT token (for duplicate detection)
 * @param productId - Product ID (for duplicate detection)
 * @param description - Route description
 * @param date - Service date for duplicate detection (optional)
 * @param timezone - Timezone for duplicate detection (optional)
 * @returns Complete validation result
 */
export async function validateRoutesForUpload(
  routes: FixedRoute[],
  stops: FixedRouteStop[],
  networkId: number,
  jwtToken: string,
  productId: string,
  description: string = '',
  date?: string,
  timezone?: string
): Promise<ValidationResult> {
  const errors: string[] = [];
  const warnings: string[] = [];
  const allDuplicates: DuplicateRoute[] = [];

  // Step 1: Field validation
  for (const route of routes) {
    const exportedData = exportRouteData(route, stops);
    const validation = validateRouteExport(exportedData);

    if (!validation.isValid) {
      errors.push(`Route "${route.name}": ${validation.errors.join('; ')}`);
    }

    if (validation.warnings.length > 0) {
      warnings.push(`Route "${route.name}": ${validation.warnings.join('; ')}`);
    }
  }

  // Step 2: Dry-run payload generation
  const dryRunPayloads = generateDryRunPayloads(routes, stops, networkId, description);

  // Step 3: Duplicate detection (if date/timezone provided)
  if (date && timezone) {
    const duplicates = await checkForDuplicates(jwtToken, productId, routes, date, timezone);
    allDuplicates.push(...duplicates);

    if (duplicates.length > 0) {
      warnings.push(
        `Found ${duplicates.length} duplicate route(s) in UAT. Consider incrementing version numbers.`
      );
    }
  }

  // Step 4: Generate version name suggestions
  const suggestedVersionNames = generateVersionNames(routes, allDuplicates);

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    duplicates: allDuplicates,
    dryRunPayloads,
    suggestedVersionNames,
  };
}

/**
 * Coordinate matching tolerance in meters
 * Stops within this distance are considered the same location
 */
const COORDINATE_TOLERANCE_METERS = 10;

/**
 * Calculate distance between two coordinates in meters (Haversine formula)
 */
function calculateDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  const R = 6371000; // Earth's radius in meters
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

/**
 * Result of coordinate matching for a single stop
 */
export interface StopMatchResult {
  atlasStopId: string;
  atlasStop: FixedRouteStop;
  matchedUATStopId: string | null;
  matchedUATStop: UATStop | null;
  distanceMeters: number | null;
  withinTolerance: boolean;
}

/**
 * Match Atlas stops to existing UAT stops by coordinates
 * 
 * @param atlasStops - Stops from Atlas (without UAT stop IDs)
 * @param uatStops - Existing stops from target network
 * @param toleranceMeters - Distance tolerance in meters (default: 10)
 * @returns Array of match results for each Atlas stop
 */
export function matchStopsByCoordinates(
  atlasStops: FixedRouteStop[],
  uatStops: Map<string, UATStop> | UATStop[],
  toleranceMeters: number = COORDINATE_TOLERANCE_METERS
): StopMatchResult[] {
  const results: StopMatchResult[] = [];
  
  // Convert UAT stops array to Map if needed
  const uatStopsMap = Array.isArray(uatStops)
    ? new Map(uatStops.map(stop => [stop.id, stop]))
    : uatStops;

  // Iterate through Atlas stops
  for (const atlasStop of atlasStops) {
    // Skip stops that already have UAT stop IDs
    if (atlasStop.uatStopId) {
      results.push({
        atlasStopId: atlasStop.id,
        atlasStop,
        matchedUATStopId: atlasStop.uatStopId,
        matchedUATStop: uatStopsMap.get(atlasStop.uatStopId) || null,
        distanceMeters: null,
        withinTolerance: true,
      });
      continue;
    }

    // Skip stops without coordinates
    if (!atlasStop.coordinates || 
        !atlasStop.coordinates.lat || 
        !atlasStop.coordinates.lng) {
      results.push({
        atlasStopId: atlasStop.id,
        atlasStop,
        matchedUATStopId: null,
        matchedUATStop: null,
        distanceMeters: null,
        withinTolerance: false,
      });
      continue;
    }

    // Find closest match within tolerance
    let closestMatch: { stop: UATStop; distance: number } | null = null;
    
    // Convert Map to array for iteration (compatible with older TypeScript targets)
    const uatStopsArray = Array.from(uatStopsMap.values());
    
    for (const uatStop of uatStopsArray) {
      if (!uatStop.coordinates || !uatStop.coordinates.lat || !uatStop.coordinates.lng) {
        continue;
      }

      const distance = calculateDistance(
        atlasStop.coordinates.lat,
        atlasStop.coordinates.lng,
        uatStop.coordinates.lat,
        uatStop.coordinates.lng
      );

      if (distance <= toleranceMeters) {
        if (!closestMatch || distance < closestMatch.distance) {
          closestMatch = { stop: uatStop, distance };
        }
      }
    }

    // Add result
    if (closestMatch) {
      results.push({
        atlasStopId: atlasStop.id,
        atlasStop,
        matchedUATStopId: closestMatch.stop.id,
        matchedUATStop: closestMatch.stop,
        distanceMeters: closestMatch.distance,
        withinTolerance: true,
      });
    } else {
      results.push({
        atlasStopId: atlasStop.id,
        atlasStop,
        matchedUATStopId: null,
        matchedUATStop: null,
        distanceMeters: null,
        withinTolerance: false,
      });
    }
  }

  return results;
}

/**
 * Compare two sets of stops to determine if they are the same or different
 * 
 * Stop changes include:
 * - Adding stops
 * - Removing stops
 * - Changing stop order
 * - Changing stop times (timeToNextStop, timeSpentAtStop)
 * - Changing stop coordinates (beyond tolerance)
 * - Changing stop IDs (using different stops)
 * 
 * @param stops1 - First set of stops (Atlas route stops)
 * @param stops2 - Second set of stops (UAT route stops)
 * @param coordinateToleranceMeters - Coordinate matching tolerance in meters (default: 10)
 * @returns true if stops are different (changed), false if same
 */
export function compareStops(
  stops1: Array<{
    stopId?: string;
    uatStopId?: string;
    order: number;
    timeToNextStop: number;
    timeSpentAtStop: number;
    coordinates?: { lat: number; lng: number };
  }>,
  stops2: Array<{
    id?: string;
    order: number;
    timeToNextStop: number;
    timeSpentAtStop: number;
    coordinates?: { lat: number; lng: number };
  }>,
  coordinateToleranceMeters: number = COORDINATE_TOLERANCE_METERS
): boolean {
  // Compare stop count
  if (stops1.length !== stops2.length) {
    return true; // Different count = changed
  }

  // Sort both arrays by order for consistent comparison
  const sortedStops1 = [...stops1].sort((a, b) => a.order - b.order);
  const sortedStops2 = [...stops2].sort((a, b) => a.order - b.order);

  // Compare each stop in order
  for (let i = 0; i < sortedStops1.length; i++) {
    const stop1 = sortedStops1[i];
    const stop2 = sortedStops2[i];

    // Compare stop order
    if (stop1.order !== stop2.order) {
      return true; // Different order = changed
    }

    // Compare stop IDs (if available)
    const stop1Id = stop1.uatStopId || stop1.stopId;
    const stop2Id = stop2.id;
    if (stop1Id && stop2Id && stop1Id !== stop2Id) {
      return true; // Different stop IDs = changed
    }

    // Compare stop times
    if (stop1.timeToNextStop !== stop2.timeToNextStop) {
      return true; // Different time to next stop = changed
    }
    if (stop1.timeSpentAtStop !== stop2.timeSpentAtStop) {
      return true; // Different time spent at stop = changed
    }

    // Compare stop coordinates (if available)
    if (stop1.coordinates && stop2.coordinates) {
      const distance = calculateDistance(
        stop1.coordinates.lat,
        stop1.coordinates.lng,
        stop2.coordinates.lat,
        stop2.coordinates.lng
      );
      if (distance > coordinateToleranceMeters) {
        return true; // Coordinates differ beyond tolerance = changed
      }
    } else if (stop1.coordinates || stop2.coordinates) {
      // One has coordinates, the other doesn't = changed
      return true;
    }
  }

  // All stops match
  return false;
}

/**
 * Compare route names (case-sensitive)
 * 
 * @param name1 - First route name
 * @param name2 - Second route name
 * @returns true if names match (case-sensitive), false otherwise
 */
export function compareRouteNames(name1: string, name2: string): boolean {
  return name1 === name2; // Case-sensitive comparison
}

/**
 * Find routes in target network with matching name
 * 
 * @param routeName - Route name to search for
 * @param targetRoutes - Routes from target network
 * @returns Array of matching routes
 */
export function findMatchingRoutes(
  routeName: string,
  targetRoutes: Array<{ id: string; name: string; version?: string }>
): Array<{ id: string; name: string; version?: string }> {
  return targetRoutes.filter(route => compareRouteNames(route.name, routeName));
}

/**
 * Extract version number from version name string
 * 
 * @param versionName - Version name (e.g., "Version 1", "Version 2", "v1")
 * @returns Version number (1, 2, etc.) or 0 if not found
 */
export function extractVersionNumber(versionName: string | undefined): number {
  if (!versionName) return 0;
  
  // Match patterns like "Version 1", "Version 2", "v1", "v2", etc.
  const match = versionName.match(/(?:Version\s+)?(\d+)/i);
  return match ? parseInt(match[1], 10) : 0;
}

/**
 * Generate next version name for a route
 * 
 * @param routeName - Route name
 * @param targetRoutes - Routes from target network
 * @returns Next version name (e.g., "Version 2", "Version 3")
 */
export function generateNextVersionName(
  routeName: string,
  targetRoutes: Array<{ id: string; name: string; version?: string }>
): string {
  // Find all routes with matching name
  const matchingRoutes = findMatchingRoutes(routeName, targetRoutes);
  
  // Extract version numbers
  const versionNumbers = matchingRoutes
    .map(route => extractVersionNumber(route.version))
    .filter(n => !isNaN(n) && n > 0);
  
  // Find highest version number
  const maxVersion = versionNumbers.length > 0 ? Math.max(...versionNumbers) : 0;
  
  // Increment and return
  return `Version ${maxVersion + 1}`;
}

/**
 * Result of stop change detection for a route
 */
export interface StopChangeDetectionResult {
  routeId: string;
  routeName: string;
  stopsChanged: boolean;
  existingRouteId?: string;
  existingRouteVersion?: string;
}

/**
 * Detect if stops have changed for selected routes
 * 
 * @param atlasRoutes - Routes from Atlas
 * @param targetRoutes - Routes from target network (with stops)
 * @param atlasStops - All stops from Atlas
 * @param coordinateToleranceMeters - Coordinate matching tolerance in meters
 * @returns Array of change detection results
 */
export function detectStopChanges(
  atlasRoutes: Array<{
    id: string;
    name: string;
    stops: Array<{
      stopId: string;
      order: number;
      timeToNextStop: number;
      timeSpentAtStop: number;
    }>;
  }>,
  targetRoutes: Array<{
    id: string;
    name: string;
    version?: string;
    stops: Array<{
      id: string;
      order: number;
      timeToNextStop: number;
      timeSpentAtStop: number;
      coordinates?: { lat: number; lng: number };
    }>;
  }>,
  atlasStops: Array<{
    id: string;
    uatStopId?: string;
    coordinates?: { lat: number; lng: number };
  }>,
  coordinateToleranceMeters: number = COORDINATE_TOLERANCE_METERS
): StopChangeDetectionResult[] {
  const results: StopChangeDetectionResult[] = [];

  for (const atlasRoute of atlasRoutes) {
    // Find matching route in target network
    const matchingRoutes = findMatchingRoutes(atlasRoute.name, targetRoutes);
    
    if (matchingRoutes.length === 0) {
      // No matching route - new route, no conflict
      results.push({
        routeId: atlasRoute.id,
        routeName: atlasRoute.name,
        stopsChanged: false, // New route, no changes to detect
      });
      continue;
    }

    // Use the first matching route (or latest version - could be enhanced)
    const existingRoute = targetRoutes.find(r => r.id === matchingRoutes[0].id);
    if (!existingRoute) {
      results.push({
        routeId: atlasRoute.id,
        routeName: atlasRoute.name,
        stopsChanged: false,
      });
      continue;
    }

    // Map Atlas stops to include coordinates and UAT stop IDs
    const atlasRouteStops = atlasRoute.stops.map(stopConfig => {
      const stop = atlasStops.find(s => s.id === stopConfig.stopId);
      return {
        stopId: stopConfig.stopId,
        uatStopId: stop?.uatStopId,
        order: stopConfig.order,
        timeToNextStop: stopConfig.timeToNextStop,
        timeSpentAtStop: stopConfig.timeSpentAtStop,
        coordinates: stop?.coordinates,
      };
    });

    // Compare stops
    const stopsChanged = compareStops(
      atlasRouteStops,
      existingRoute.stops,
      coordinateToleranceMeters
    );

    results.push({
      routeId: atlasRoute.id,
      routeName: atlasRoute.name,
      stopsChanged,
      existingRouteId: existingRoute.id,
      existingRouteVersion: existingRoute.version,
    });
  }

  return results;
}

